/**
 * Pick notation.
 *
 * The league writes picks as "9.7" — round, then the slot within that round — and that is how
 * they appear in the Trade Log, in Discord, and on the board. Two things make it slightly less
 * obvious than it looks:
 *
 *   The draft snakes, so a GM in slot 3 picks 3rd in odd rounds and 8th in even ones.
 *
 *   A pick's number belongs to the ORIGINAL owner's slot, not the current holder's. When Rajan
 *   holds John's 14th, it is John's slot that decides whether that is 14.02 or 14.09. Getting
 *   this backwards would put keepers in the wrong place on the board.
 */

/** Where a GM sits in the round-one order, 1-based. Null when the order is not set yet. */
export function slotOf(order: readonly string[], gm: string): number | null {
  const i = order.findIndex((g) => g.toUpperCase() === gm.toUpperCase());
  return i < 0 ? null : i + 1;
}

/** Which pick within the round a slot makes, accounting for the snake. */
export function pickInRound(round: number, slot: number, teams: number): number {
  return round % 2 === 1 ? slot : teams - slot + 1;
}

export function overallPick(round: number, slot: number, teams: number): number {
  return (round - 1) * teams + pickInRound(round, slot, teams);
}

/**
 * "9.07". Falls back to "9.——" when the draft order has not been set, because a made-up slot
 * on a keeper declaration is worse than an honest blank.
 */
export function pickLabel(
  round: number,
  slot: number | null,
  teams: number,
): string {
  if (slot === null) return `${round}.——`;
  return `${round}.${String(pickInRound(round, slot, teams)).padStart(2, '0')}`;
}

/**
 * Which band of the Grid a round value sits in. Rounds 1-2 cannot be kept at all; 3-9 is the
 * franchise tag's eligible band (2025 P05 §3); 10 and later is where the sleeper reward lives
 * (2017-II-2 §2). Every keeper decision in the app turns on which of these three a player is
 * in, so the UI colours them rather than making people remember the numbers.
 */
export type RoundBand = 'unkeepable' | 'franchise' | 'sleeper';

export function roundBand(round: number): RoundBand {
  if (round <= 2) return 'unkeepable';
  if (round <= 9) return 'franchise';
  return 'sleeper';
}

/**
 * The bands as brackets over a board of `rounds` rows — one span per band, in order. The rail
 * beside the draftboard draws these; `roundBand` stays the authority on where the lines fall.
 */
export interface RoundBandSpan { band: RoundBand; from: number; to: number; label: string; title: string }

/*
 * WHAT THE BRACKET SAYS ON THE BOARD (17a rev C). The rail names the band a round sits in, and
 * the shortest true name is the one the room uses: rounds 3–9 are where a franchise tag can be
 * spent, rounds 10 up are where the sleeper reward lives. `BAND_LABEL` below carries the rule
 * itself, on the bracket's tooltip, the right way round — a player DRAFTED in rounds 1–2 cannot
 * be kept, though a keeper may still be charged to one of those picks by escalation.
 */
export const BAND_SHORT: Record<RoundBand, string> = {
  unkeepable: 'Can’t keep',
  franchise: 'Franchise-eligible',
  sleeper: 'Sleeper-eligible',
};

export function roundBands(rounds: number): RoundBandSpan[] {
  const out: RoundBandSpan[] = [];
  for (let r = 1; r <= rounds; r += 1) {
    const band = roundBand(r);
    const last = out[out.length - 1];
    if (last && last.band === band) { last.to = r; continue; }
    out.push({ band, from: r, to: r, label: BAND_SHORT[band], title: BAND_LABEL[band] });
  }
  return out;
}

export const BAND_LABEL: Record<RoundBand, string> = {
  unkeepable: 'Rounds 1-2 — cannot be kept, and cannot be franchised either',
  franchise: 'Rounds 3-9 — the only band the franchise tag can touch (2025 P05 §3)',
  sleeper: 'Rounds 10+ — sleeper reward territory, and outside the franchise band (2017-II-2 §2)',
};
